import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import phone from "../assets/phone.png";
import { get150Coins } from "../functions/get150Coins";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Eye,
  GitBranch,
  Radio,
  Search,
  ShieldAlert,
  Wallet,
  Zap,
} from "lucide-react";

const WS_URL = "wss://cryptospace.network/wallet-feed";

const HIGH_PRIORITY_EVENTS = new Set([
  "FIRST_TOKEN_ACTIVITY",
  "NEW_CONTRACT_DEPLOY",
  "LARGE_VALUE_TX",
  "BOT_LIKE_TX",
  "GAS_SPIKE",
  "COMPLEX_TX",
]);

const EVENT_COPY = {
  FIRST_TOKEN_ACTIVITY: {
    title: "New token activity",
    insight: "A token is showing its first meaningful on-chain movement.",
    severity: "high",
  },
  NEW_CONTRACT_DEPLOY: {
    title: "New contract deployed",
    insight: "Fresh contract activity can mark a launch, setup, or test phase.",
    severity: "high",
  },
  LARGE_VALUE_TX: {
    title: "Large value movement",
    insight: "A wallet moved size that may matter for liquidity or intent.",
    severity: "high",
  },
  BOT_LIKE_TX: {
    title: "Automated behavior",
    insight: "The pattern looks systematic rather than manual.",
    severity: "medium",
  },
  GAS_SPIKE: {
    title: "Gas pressure",
    insight: "Priority execution pressure is rising around this activity.",
    severity: "medium",
  },
  REAL_SWAP: {
    title: "Real swap",
    insight: "A meaningful token swap was detected in the live flow.",
    severity: "medium",
  },
  CONTRACT_CALL: {
    title: "Contract interaction",
    insight: "A wallet interacted with contract logic.",
    severity: "low",
  },
};

const FALLBACK_MARKET_COINS = [
  marketCoin("bitcoin", "btc", "Bitcoin", 79908, 3.22, 56253699001, "https://assets.coingecko.com/coins/images/1/large/bitcoin.png"),
  marketCoin("ethereum", "eth", "Ethereum", 2489.51, 1.14, 20357039308, "https://assets.coingecko.com/coins/images/279/large/ethereum.png"),
  marketCoin("tether", "usdt", "Tether", 1, 0, 91854276834, "https://assets.coingecko.com/coins/images/325/large/Tether.png"),
  marketCoin("ripple", "xrp", "XRP", 1.49, 1.01, 5591760456, "https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png"),
  marketCoin("binancecoin", "bnb", "BNB", 703, 0.65, 1292869261, "https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png"),
  marketCoin("usd-coin", "usdc", "USDC", 1, 0.01, 24168081297, "https://assets.coingecko.com/coins/images/6319/large/USD_Coin_icon.png"),
];

function marketCoin(id, symbol, name, current_price, price_change_percentage_24h, total_volume, image) {
  return { id, symbol, name, current_price, price_change_percentage_24h, total_volume, image };
}

function shortAddress(value) {
  if (!value) return "Unknown";
  if (value.length <= 14) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function getWallet(signal) {
  const payload = signal.payload || {};
  return payload.from || payload.to || signal.from || signal.to || null;
}

function getContract(signal) {
  const payload = signal.payload || {};
  return payload.contract || payload.token || payload.to || signal.contract || null;
}

function getAmount(signal) {
  const payload = signal.payload || {};
  return Number(payload.amountIn || payload.amount || payload.value || 0) || 0;
}

function getEventMeta(event) {
  return EVENT_COPY[event] || {
    title: event || "Unknown signal",
    insight: "Live on-chain activity detected. Open raw data for details.",
    severity: "low",
  };
}

function getSeverityScore(signal) {
  const event = signal.event;
  const base = HIGH_PRIORITY_EVENTS.has(event) ? 70 : 30;
  const amount = getAmount(signal);
  const valueScore = amount > 0 ? Math.min(25, Math.log10(amount + 1) * 4) : 0;
  return Math.round(base + valueScore);
}

export default function OverviewPage() {
  const [signals, setSignals] = useState([]);
  const [marketCoins, setMarketCoins] = useState(FALLBACK_MARKET_COINS);
  const [status, setStatus] = useState("connecting");
  const signalBuffer = useRef([]);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => setStatus("live");
    ws.onclose = () => setStatus("offline");
    ws.onerror = () => setStatus("degraded");

    ws.onmessage = (event) => {
      try {
        const signal = JSON.parse(event.data);
        signalBuffer.current.push({ ...signal, receivedAt: Date.now() });
      } catch {
        // Keep the overview focused on structured signals.
      }
    };

    return () => ws.close();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!signalBuffer.current.length) return;

      const nextSignals = signalBuffer.current.splice(0);
      setSignals((prev) => [...nextSignals.reverse(), ...prev].slice(0, 300));
    }, 250);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;

    get150Coins().then((coins) => {
      if (!isMounted || !Array.isArray(coins) || !coins.length) return;
      setMarketCoins(coins.slice(0, 6));
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const intelligence = useMemo(() => {
    const wallets = new Map();
    const contracts = new Map();
    const events = new Map();
    const recentCritical = [];

    signals.forEach((signal) => {
      const wallet = getWallet(signal);
      const contract = getContract(signal);
      const event = signal.event || "UNKNOWN";
      const score = getSeverityScore(signal);

      events.set(event, (events.get(event) || 0) + 1);

      if (wallet) {
        const current = wallets.get(wallet) || {
          wallet,
          score: 0,
          count: 0,
          events: new Map(),
          lastSeen: signal.receivedAt,
        };

        current.score += score;
        current.count += 1;
        current.lastSeen = Math.max(current.lastSeen, signal.receivedAt);
        current.events.set(event, (current.events.get(event) || 0) + 1);
        wallets.set(wallet, current);
      }

      if (contract) {
        const current = contracts.get(contract) || {
          contract,
          wallets: new Set(),
          count: 0,
          score: 0,
        };

        if (wallet) current.wallets.add(wallet);
        current.count += 1;
        current.score += score;
        contracts.set(contract, current);
      }

      if (score >= 65) {
        recentCritical.push({ ...signal, score });
      }
    });

    const topWallets = Array.from(wallets.values())
      .map((wallet) => ({
        ...wallet,
        topEvent: Array.from(wallet.events.entries()).sort((a, b) => b[1] - a[1])[0]?.[0],
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    const topClusters = Array.from(contracts.values())
      .map((cluster) => ({
        ...cluster,
        walletCount: cluster.wallets.size,
      }))
      .filter((cluster) => cluster.walletCount > 1 || cluster.count > 3)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

    const eventMix = Array.from(events.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return {
      eventMix,
      topWallets,
      topClusters,
      critical: recentCritical.slice(0, 6),
      highCount: signals.filter((signal) => getSeverityScore(signal) >= 65).length,
    };
  }, [signals]);

  const totalSignals = signals.length;
  const activeWallets = new Set(signals.map(getWallet).filter(Boolean)).size;
  const activeContracts = new Set(signals.map(getContract).filter(Boolean)).size;

  return (
    <div className="overview-page">
      <Header />

      <main className="overview-shell">
        <section className="product-hero">
          <div className="product-hero-copy">
            <div className="overview-kicker">
              <Radio size={16} />
              Real-time on-chain intelligence
            </div>
            <h1>
              See Market Intent
              <span> Before Price Moves.</span>
            </h1>
            <p>
              ChainIntel turns live mempool and wallet behavior into readable
              signals for discovery, anomaly detection, and smart-money tracking.
            </p>
            <div className="product-hero-actions">
              <a href="#command-center">Open Command Center</a>
              <Link to="/signal-center">Signal Center</Link>
            </div>
          </div>

          <div className="product-hero-visual" aria-hidden="true">
            <div className="asset-node tether" />
            <div className="asset-node bnb" />
            <div className="asset-node eth" />
            <div className="asset-node btc" />
            <img src={phone} alt="" />
          </div>
        </section>

        <section className="market-snapshot">
          <div className="market-snapshot-head">
            <div>
              <div className="overview-kicker">
                <BarChart3 size={16} />
                Market context
              </div>
              <h2>Major Assets</h2>
              <p>
                The core market layer stays visible while the intelligence
                engine watches live wallet and contract behavior.
              </p>
            </div>
            <Link to="/market-tracker-classic">Open Market Tracker</Link>
          </div>

          <div className="market-snapshot-grid">
            {marketCoins.length ? (
              marketCoins.map((coin) => (
                <MarketCoinCard coin={coin} key={coin.id} />
              ))
            ) : (
              Array.from({ length: 6 }).map((_, index) => (
                <div className="market-coin-card market-coin-loading" key={index} />
              ))
            )}
          </div>
        </section>

        <section className="overview-hero">
          <div>
            <div className="overview-kicker">
              <Radio size={16} />
              Live on-chain intelligence
            </div>
            <h1 id="command-center">Command Center</h1>
            <p>
              A real-time view of market intent, wallet behavior, anomalies,
              and coordinated activity before it becomes obvious on price charts.
            </p>
          </div>

          <div className={`overview-status ${status}`}>
            <span />
            {status === "live" ? "Live feed connected" : `Feed ${status}`}
          </div>
        </section>

        <section className="overview-stats">
          <Metric icon={<Activity />} label="Signals tracked" value={totalSignals} />
          <Metric icon={<AlertTriangle />} label="High priority" value={intelligence.highCount} />
          <Metric icon={<Wallet />} label="Active wallets" value={activeWallets} />
          <Metric icon={<GitBranch />} label="Contracts watched" value={activeContracts} />
        </section>

        <section className="overview-grid">
          <Panel
            title="Critical Signals"
            icon={<ShieldAlert />}
            actionLabel="Open feed"
            actionTo="/signal-center"
            wide
          >
            {intelligence.critical.length ? (
              intelligence.critical.map((signal, index) => (
                <SignalRow key={`${signal.receivedAt}-${index}`} signal={signal} />
              ))
            ) : (
              <EmptyState text="Waiting for high-priority live signals." />
            )}
          </Panel>

          <Panel title="Smart Wallets" icon={<Wallet />} actionLabel="Investigate" actionTo="/smart-money">
            {intelligence.topWallets.length ? (
              intelligence.topWallets.map((wallet) => (
                <div className="overview-list-row" key={wallet.wallet}>
                  <div>
                    <strong>{shortAddress(wallet.wallet)}</strong>
                    <span>{wallet.topEvent || "Live activity"}</span>
                  </div>
                  <b>{wallet.score}</b>
                </div>
              ))
            ) : (
              <EmptyState text="Wallet ranking will appear as activity arrives." />
            )}
          </Panel>

          <Panel title="Anomaly Radar" icon={<BarChart3 />} actionLabel="View anomalies" actionTo="/anomaly">
            <div className="overview-anomaly">
              <strong>{intelligence.highCount}</strong>
              <span>high-priority events in the current memory window</span>
            </div>
            <div className="overview-event-mix">
              {intelligence.eventMix.map(([event, count]) => (
                <div key={event}>
                  <span>{event}</span>
                  <b>{count}</b>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Coordinated Clusters" icon={<GitBranch />} actionLabel="Open graph" actionTo="/graph">
            {intelligence.topClusters.length ? (
              intelligence.topClusters.map((cluster) => (
                <div className="overview-list-row" key={cluster.contract}>
                  <div>
                    <strong>{shortAddress(cluster.contract)}</strong>
                    <span>{cluster.walletCount} wallets, {cluster.count} events</span>
                  </div>
                  <b>{cluster.score}</b>
                </div>
              ))
            ) : (
              <EmptyState text="Clusters will form when wallets converge on contracts." />
            )}
          </Panel>

          <Panel title="Discovery" icon={<Search />} actionLabel="Open signals" actionTo="/signals">
            <div className="overview-discovery">
              <Zap size={28} />
              <div>
                <strong>Watch for first activity and new contracts.</strong>
                <span>
                  These are the earliest product signals for token discovery,
                  launches, and suspicious setup phases.
                </span>
              </div>
            </div>
          </Panel>

          <Panel title="Investigation Tools" icon={<Eye />} actionLabel="Raw mode" actionTo="/raw">
            <div className="overview-tool-links">
              <Link to="/behavior-deep">Behavior Deep</Link>
              <Link to="/clusters">Clusters</Link>
              <Link to="/event-flow">Event Flow</Link>
              <Link to="/pulse">Network Pulse</Link>
            </div>
          </Panel>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function MarketCoinCard({ coin }) {
  const isUp = coin.price_change_percentage_24h >= 0;

  return (
    <Link to={`/coin/${coin.id}`} className="market-coin-card">
      <div className="market-coin-head">
        <img src={coin.image} alt={coin.name} />
        <div>
          <strong>{coin.symbol.toUpperCase()}</strong>
          <span>{coin.name}</span>
        </div>
      </div>

      <div className="market-coin-price">
        ${coin.current_price.toLocaleString()}
      </div>

      <div className={isUp ? "market-coin-change up" : "market-coin-change down"}>
        {isUp ? "+" : ""}
        {coin.price_change_percentage_24h.toFixed(2)}%
      </div>

      <div className="market-coin-meta">
        <span>Volume</span>
        <b>${coin.total_volume.toLocaleString()}</b>
      </div>
    </Link>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="overview-metric">
      <div>{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Panel({ title, icon, actionLabel, actionTo, children, wide }) {
  return (
    <section className={wide ? "overview-panel overview-panel-wide" : "overview-panel"}>
      <div className="overview-panel-head">
        <h2>
          {icon}
          {title}
        </h2>
        {actionTo && <Link to={actionTo}>{actionLabel}</Link>}
      </div>
      <div className="overview-panel-body">{children}</div>
    </section>
  );
}

function SignalRow({ signal }) {
  const meta = getEventMeta(signal.event);
  const wallet = getWallet(signal);
  const contract = getContract(signal);

  return (
    <div className={`overview-signal-row ${meta.severity}`}>
      <div>
        <strong>{meta.title}</strong>
        <span>{meta.insight}</span>
      </div>
      <div className="overview-signal-meta">
        <span>{shortAddress(wallet || contract)}</span>
        <b>{signal.score}</b>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="overview-empty">{text}</div>;
}
